// Admin-Seite Mieterselbstauskunft — Eingänge aus dem öffentlichen Formular
// (/selbstauskunft/:slug) plus Verwaltung der Formular-Links.
//
// Neugestaltung nach design_handoff_selbstauskunft (17.10.2026): Apple-/Widget-Stil im globalen
// Hell/Dunkel. Status-Widgets (Filter + Zähler), Filterzeile (Objekt, Zeitraum) auf der Seite UND
// weiter in der Pille (gleiche State-Quelle). Ab 1024 px Liste | Eingang nebeneinander, darunter
// Liste ODER Eingang mit „Alle Eingänge“-Rücksprung.
//
// Link-Verwaltung als Sheet über der Seite; Kontakt anlegen weiter im Pillen-Formular (openForm).
//
// Ticket #375 (Björn): Die rechte Spalte bleibt beim Scrollen der Liste stehen (sticky, eigener
// Scrollbereich), dazu Status „Eingeladen", interne Notiz und „Kontakt anlegen" mit Vorbelegung.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import type { SteuerungsEintrag } from '../../../components/island/widgets/typen';
import { AddressBook, ArrowCounterClockwise, Buildings, CalendarBlank, CaretLeft, LinkSimple, Trash } from '@phosphor-icons/react';
import SelbstauskunftLinksVerwaltung from './SelbstauskunftLinksVerwaltung';
import KontaktErstellenFormular from '../kontakte/KontaktErstellenFormular';
import { kontaktAusSelbstauskunft } from '../../../lib/selbstauskunft/kontakt';
import {
  filtereEingaenge, tagVon,
  type SelbstauskunftStatus,
} from '../../../lib/selbstauskunft/status';
import {
  zeitraumBereich, zeitraumText, ZEITRAUM_LABEL, ZEITRAUM_PRESETS,
  type ZeitraumPreset,
} from '../../../lib/selbstauskunft/zeitraum';
import type { Eingang } from './teile/typen';
import { EingangAnsicht, KeinEingang } from './teile/Eingang';
import { BestaetigenDialog, SchwebenderToast, useToastPill } from '../../../components/immobilien/ui';
import { SeitenKopf, StatusWidgets, FilterZeile } from './teile/Uebersicht';
import { EingangsListe } from './teile/EingangsListe';
import { aktiverZeitraum, istZweispaltig, zaehleJeStatus } from '../../../lib/selbstauskunft/darstellung';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useViewport } from '../../../components/island/hooks';
import { useMobilModus } from '../../../lib/immobilien/mobil';

type Status = SelbstauskunftStatus;

const SelbstauskunftListe: React.FC = () => {
  const [eingaenge, setEingaenge] = useState<Eingang[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | 'alle'>('alle');
  const [objektFilter, setObjektFilter] = useState<string>('alle');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [eigenerZeitraum, setEigenerZeitraum] = useState(false);
  // #605: ?eingang=<id> (z. B. aus einer Meldung der Pille) wählt diesen Eingang direkt aus
  const [suchParams, setSuchParams] = useSearchParams();
  const [ausgewaehltId, setAusgewaehltId] = useState<string | null>(() => suchParams.get('eingang'));
  useEffect(() => {
    const e = suchParams.get('eingang');
    if (!e) return;
    setAusgewaehltId(e);
    // Filter zurücksetzen, sonst blendet ein alter Filter den Eingang aus
    setStatusFilter('alle'); setObjektFilter('alle'); setVon(''); setBis(''); setEigenerZeitraum(false);
    setSuchParams(p => { const n = new URLSearchParams(p); n.delete('eingang'); return n; }, { replace: true });
  }, [suchParams, setSuchParams]);
  const [wirdGeloescht, setWirdGeloescht] = useState(false);
  const [loeschKandidat, setLoeschKandidat] = useState<Eingang | null>(null);
  const [toast, zeigeToast] = useToastPill();
  const { openForm, closeForm } = useIslandActions();
  const navigate = useNavigate();
  const detailRef = useRef<HTMLDivElement>(null);
  const viewport = useViewport();
  const { mobil } = useMobilModus();
  const zweispaltig = istZweispaltig(viewport.width);

  const laden = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('selbstauskuenfte')
      .select('*, objekt:objekte(id, bezeichnung), einheit:objekt_einheiten(id, bezeichnung)')
      .order('created_at', { ascending: false });
    if (err) {
      setError(err.message);
    } else {
      setEingaenge((data as unknown as Eingang[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => { void laden(); }, []);

  // Formular-Links als Sheet über der Seite (Handoff §3); der Pillen-Eintrag öffnet dasselbe Sheet.
  const [linksOffen, setLinksOffen] = useState(false);
  const oeffneLinksVerwaltung = () => setLinksOffen(true);

  // Objekte für den Filter aus den vorhandenen Eingängen — nur was wirklich vorkommt.
  // Steht VOR usePillenSteuerung: die Pillen-Steuerung liest die Liste.
  const objektOptionen = (() => {
    const map = new Map<string, string>();
    let ohne = false;
    for (const e of eingaenge) {
      if (e.objekt_id && e.objekt?.bezeichnung) map.set(e.objekt_id, e.objekt.bezeichnung);
      else if (!e.objekt_id) ohne = true;
    }
    const liste = [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'de'));
    return { liste, ohne };
  })();

  // Objekt- und Zeitraum-Filter wohnen in der Pille (User 2026-08-10: zwei nackte
  // Datumsfelder auf der Seite waren unbequem). Der Zeitraum läuft über Voreinstellungen,
  // freie Daten erscheinen erst bei „Zeitraum".
  // WICHTIG: Die Pille bekommt ein FERTIGES Element — es friert den aktuellen Stand ein.
  // Alles, was die Steuerung anzeigt oder liest, MUSS in den Deps stehen, sonst zeigt die
  // Pille alte Werte (s. Merkregel „Stale Closure" der Pillen-Formulare).
  const heuteIso = tagVon(new Date().toISOString());
  const setzeZeitraum = (p: ZeitraumPreset) => {
    const b = zeitraumBereich(p, heuteIso);
    setVon(b.von); setBis(b.bis);
    setEigenerZeitraum(p === 'eigen');
  };
  const pillenFilterAktiv = objektFilter !== 'alle' || !!von || !!bis;
  const zeitraumWahl: ZeitraumPreset = aktiverZeitraum(von, bis, eigenerZeitraum, heuteIso);
  usePillenSteuerung({
    titel: 'Mieterselbstauskunft',
    eintraege: [
      { id: 'links', typ: 'aktion', farbe: 'gold', icon: LinkSimple, kicker: 'Links', titel: 'Links verwalten', unter: 'Selbstauskunft-Links',
        onClick: oeffneLinksVerwaltung },
      { id: 'objekt', typ: 'auswahl', farbe: 'violett', icon: Buildings, kicker: 'Filter · Objekt', wert: objektFilter, standard: 'alle',
        onChange: setObjektFilter,
        optionen: [
          { wert: 'alle', label: 'Alle Objekte' },
          ...objektOptionen.liste.map(([id, bez]) => ({ wert: id, label: bez })),
          ...(objektOptionen.ohne ? [{ wert: 'ohne', label: 'Ohne Objekt' }] : []),
        ] },
      { id: 'zeitraum', typ: 'auswahl', farbe: 'blau', icon: CalendarBlank, kicker: 'Eingang', wert: zeitraumWahl, unter: zeitraumText(von, bis),
        onChange: (w: string) => setzeZeitraum(w as ZeitraumPreset),
        optionen: ZEITRAUM_PRESETS.map(p => ({ wert: p, label: ZEITRAUM_LABEL[p] })),
        zeitraum: { sichtbar: zeitraumWahl === 'eigen', von, bis, onVon: setVon, onBis: setBis }, voll: true },
      ...(pillenFilterAktiv
        ? [{ id: 'reset', typ: 'aktion', farbe: 'grau', icon: ArrowCounterClockwise, kicker: 'Zurücksetzen', titel: 'Filter zurücksetzen', unter: 'Auf Standard',
            onClick: () => { setObjektFilter('alle'); setVon(''); setBis(''); setEigenerZeitraum(false); } } satisfies SteuerungsEintrag]
        : []),
    ],
  }, [objektFilter, von, bis, eigenerZeitraum, objektOptionen.liste.length, objektOptionen.ohne]);

  const setzeStatus = async (id: string, status: Status) => {
    const { error: err } = await supabase.from('selbstauskuenfte').update({ status }).eq('id', id);
    if (err) { zeigeToast(`Status konnte nicht gespeichert werden: ${err.message}`, true); return; }
    setEingaenge((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
  };

  const speichereNotiz = async (id: string, text: string): Promise<boolean> => {
    const notiz_intern = text.trim() ? text : null;
    const { error: err } = await supabase.from('selbstauskuenfte').update({ notiz_intern }).eq('id', id);
    if (err) { zeigeToast(`Notiz konnte nicht gespeichert werden: ${err.message}`, true); return false; }
    setEingaenge((prev) => prev.map((e) => (e.id === id ? { ...e, notiz_intern } : e)));
    return true;
  };

  // Kontakt aus der Selbstauskunft: Formular vorbelegt öffnen, nach dem Speichern die neue Id
  // an der Selbstauskunft merken — so wird aus dem Knopf „Kontakt öffnen" und kein Doppel angelegt.
  const kontaktAnlegen = (e: Eingang) => {
    openForm(
      <KontaktErstellenFormular
        initial={kontaktAusSelbstauskunft(e)}
        onDone={async (neueId) => {
          closeForm();
          if (!neueId) return;
          const { error: err } = await supabase.from('selbstauskuenfte').update({ kontakt_id: neueId }).eq('id', e.id);
          if (err) { zeigeToast(`Kontakt angelegt, aber nicht mit der Selbstauskunft verknüpft: ${err.message}`, true); return; }
          setEingaenge((prev) => prev.map((x) => (x.id === e.id ? { ...x, kontakt_id: neueId } : x)));
          zeigeToast('Kontakt angelegt und verknüpft');
        }}
      />,
      'Kontakt aus Selbstauskunft',
      720,
      { icon: AddressBook, farbe: 'violett', badge: 'plus', pflichtHinweis: true },
    );
  };

  const loeschen = async (e: Eingang) => {
    // Rückfrage kommt aus dem App-Dialog (loeschKandidat) statt window.confirm.
    setWirdGeloescht(true);
    const { error: err } = await supabase.from('selbstauskuenfte').delete().eq('id', e.id);
    setWirdGeloescht(false);
    setLoeschKandidat(null);
    if (err) { zeigeToast(`Löschen fehlgeschlagen: ${err.message}`, true); return; }
    setAusgewaehltId(null);
    zeigeToast('Selbstauskunft gelöscht');
    await laden();
  };

  // Objekt und Zeitraum wirken zusammen; die Status-Zähler zeigen, was in DIESER Auswahl
  // steckt — sonst verspricht „Neu (3)" Eingänge, die der Objektfilter längst ausblendet.
  const imBereich = filtereEingaenge(eingaenge, { objektId: objektFilter, von, bis });
  const gefiltert = filtereEingaenge(imBereich, { status: statusFilter });
  const zaehler = zaehleJeStatus(imBereich);

  const filterAktiv = objektFilter !== 'alle' || !!von || !!bis;

  // Die Auswahl lebt als ID, nicht als Kopie: nach Nachladen oder Statuswechsel zeigt der
  // rechte Bereich sonst einen eingefrorenen alten Stand.
  const index = gefiltert.findIndex((e) => e.id === ausgewaehltId);
  const ausgewaehlt = index >= 0 ? gefiltert[index] : null;
  // Die Detailspalte scrollt für sich — ein neuer Eingang beginnt oben, nicht mitten im alten.
  useEffect(() => { detailRef.current?.scrollTo({ top: 0 }); }, [ausgewaehltId]);
  const springe = (richtung: -1 | 1) => {
    if (index < 0) return;
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= gefiltert.length) return;
    setAusgewaehltId(gefiltert[ziel].id);
  };

  const filterZuruecksetzen = () => { setObjektFilter('alle'); setVon(''); setBis(''); setEigenerZeitraum(false); };
  const filterText = filterAktiv
    ? [objektFilter !== 'alle' ? (objektFilter === 'ohne' ? 'Ohne Objekt' : (objektOptionen.liste.find(([id]) => id === objektFilter)?.[1] || 'Objekt')) : '',
       von || bis ? zeitraumText(von, bis) : ''].filter(Boolean).join(' · ')
    : null;

  const liste = (
    <EingangsListe laedt={loading} fehler={error} gesamt={eingaenge.length} gefiltert={gefiltert} filterAktiv={filterAktiv}
      ausgewaehltId={ausgewaehltId} onWahl={setAusgewaehltId} onNeuLaden={() => { void laden(); }} onLinks={oeffneLinksVerwaltung} />
  );

  // ── Eingang (rechte Spalte) ───────────────────────────────────────────────
  const eingangsInhalt = ausgewaehlt ? (
    <EingangAnsicht
      e={ausgewaehlt}
      position={index + 1}
      anzahl={gefiltert.length}
      onVorher={() => springe(-1)}
      onNaechster={() => springe(1)}
      onStatus={(st) => { void setzeStatus(ausgewaehlt.id, st); }}
      onKontaktAnlegen={() => kontaktAnlegen(ausgewaehlt)}
      onKontaktOeffnen={() => navigate(`/admin/kontakte/kontakt/${ausgewaehlt.kontakt_id}`)}
      onLoeschen={() => setLoeschKandidat(ausgewaehlt)}
      wirdGeloescht={wirdGeloescht}
      onNotiz={(text) => speichereNotiz(ausgewaehlt.id, text)}
      mobil={mobil}
    />
  ) : <KeinEingang />;

  // ── Seite ─────────────────────────────────────────────────────────────────

  return (
    <>
      <GlasTooltipSchicht />
      <SeitenKopf anzahl={eingaenge.length} filterText={filterText} mobil={mobil} onReset={filterZuruecksetzen} onLinks={oeffneLinksVerwaltung} />
      <StatusWidgets zaehler={zaehler} aktiv={statusFilter} onWahl={setStatusFilter} />
      <FilterZeile
        objektWert={objektFilter}
        objektOptionen={[
          { wert: 'alle', label: 'Alle Objekte' },
          ...objektOptionen.liste.map(([id, bez]) => ({ wert: id, label: bez })),
          ...(objektOptionen.ohne ? [{ wert: 'ohne', label: 'Ohne Objekt' }] : []),
        ]}
        onObjekt={setObjektFilter}
        zeitraum={zeitraumWahl}
        onZeitraum={setzeZeitraum}
        von={von} bis={bis} onVon={setVon} onBis={setBis}
        mobil={mobil}
      />

      {/* Ab 1024 px Liste | Eingang; rechts sticky mit eigenem Scroll (#375). Kein overflow:hidden
          auf den Vorfahren — sonst wirkt sticky nicht. */}
      {zweispaltig ? (
        <div className="grid items-start" style={{ gridTemplateColumns: '344px minmax(0,1fr)', gap: 14 }}>
          {liste}
          <div ref={detailRef} className="immo-scroll overflow-y-auto"
            style={{ position: 'sticky', top: 20, maxHeight: 'calc(100vh - 40px)', overscrollBehavior: 'contain', borderRadius: 26 }}>
            {eingangsInhalt}
          </div>
        </div>
      ) : ausgewaehlt ? (
        // overflow-x: clip — das Hereingleiten des Eingangs darf auf dem Handy kein waagerechtes Scrollen auslösen
        <div className="flex flex-col" style={{ gap: 14, overflowX: 'clip', overflowClipMargin: 20 }}>
          <button type="button" onClick={() => setAusgewaehltId(null)} className="immo-glas inline-flex items-center self-start"
            style={{ height: 44, padding: '0 16px 0 12px', gap: 6, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>
            <CaretLeft size={14} weight="bold" /> Alle Eingänge
          </button>
          {eingangsInhalt}
        </div>
      ) : liste}
      {loeschKandidat && (
        <BestaetigenDialog titel="Selbstauskunft löschen?" icon={Trash} bestaetigenLabel="Löschen" laeuft={wirdGeloescht}
          text={`Selbstauskunft von „${loeschKandidat.name}“ wirklich unwiderruflich löschen?`}
          onAbbrechen={() => setLoeschKandidat(null)} onBestaetigen={() => { void loeschen(loeschKandidat); }} />
      )}
      {linksOffen && <SelbstauskunftLinksVerwaltung onSchliessen={() => setLinksOffen(false)} onToast={zeigeToast} />}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </>
  );
};

export default SelbstauskunftListe;
